/**
 * Trust Industrie - Collection Promo Banner v3
 * Match les promos avec les collections dans les tabs
 */

(function() {
  'use strict';

  const API_BASE = 'https://trust-logistics.vercel.app/api/discounts';
  const DEBUG = true;

  function log(...args) {
    if (DEBUG) console.log('[Trust-Promo-v3]', ...args);
  }

  // Cache des promos
  let promosCache = null;
  let customizationsCache = null;

  // Normaliser texte pour comparaison
  function normalize(text) {
    if (!text) return '';
    return text.toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .trim();
  }

  // Mettre à jour le timer
  function updatePromoTimer(timerEl, endDate) {
    if (!endDate) {
      timerEl.style.display = 'none';
      return;
    }
    
    const end = new Date(endDate).getTime();
    
    function update() {
      const now = new Date().getTime();
      const distance = end - now;
      
      if (distance < 0) {
        timerEl.textContent = 'Offre expirée';
        return;
      }
      
      const days = Math.floor(distance / (1000 * 60 * 60 * 24));
      const hours = Math.floor((distance % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
      const minutes = Math.floor((distance % (1000 * 60 * 60)) / (1000 * 60));
      
      let timeStr = '⏱ Se termine dans : ';
      if (days > 0) {
        timeStr += `${days}j ${hours}h`;
      } else if (hours > 0) {
        timeStr += `${hours}h ${minutes}m`;
      } else {
        timeStr += `${minutes}m`;
      }
      
      timerEl.textContent = timeStr;
      timerEl.style.display = 'block';
    }
    
    update();
    setInterval(update, 60000); // Mise à jour chaque minute
  }

  // Récupérer toutes les promos actives
  async function fetchActivePromos() {
    if (promosCache) return promosCache;
    
    try {
      const res = await fetch(API_BASE + '?shop=prod');
      const data = await res.json();
      promosCache = data.discounts?.filter(p => p.status === 'ACTIVE') || [];
      log('Promos actives récupérées:', promosCache.length);
      return promosCache;
    } catch (error) {
      log('Erreur fetch promos:', error);
      return [];
    }
  }

  // Récupérer les personnalisations
  async function fetchCustomizations() {
    if (customizationsCache) return customizationsCache;
    
    try {
      const res = await fetch(API_BASE + '/terms');
      const data = await res.json();
      customizationsCache = data.terms || {};
      log('Personnalisations récupérées:', Object.keys(customizationsCache).length);
      return customizationsCache;
    } catch (error) {
      log('Erreur fetch customizations:', error);
      return {};
    }
  }

  // Trouver la promo pour une collection
  function findPromoForCollection(promos, collectionHandle, collectionTitle) {
    if (!promos || promos.length === 0) return null;
    if (!collectionHandle && !collectionTitle) return null;

    const handleNorm = normalize(collectionHandle);
    const titleNorm = normalize(collectionTitle);

    log('Recherche promo pour:', collectionHandle, '/', collectionTitle);

    for (const promo of promos) {
      // 1. Vérifier via targetItems (collections ciblées)
      if (promo.targetItems && Array.isArray(promo.targetItems)) {
        for (const item of promo.targetItems) {
          const itemHandleNorm = normalize(item.handle);
          const itemTitleNorm = normalize(item.title);

          if (itemHandleNorm && handleNorm && itemHandleNorm === handleNorm) {
            log('� Match trouvé (handle):', promo.title, 'pour', collectionTitle);
            return promo;
          }
          if (itemTitleNorm && titleNorm && itemTitleNorm === titleNorm) {
            log('� Match trouvé (title):', promo.title, 'pour', collectionTitle);
            return promo;
          }
        }
      }

      // 2. Vérifier via targetLabel
      if (promo.targetLabel) {
        const labelNorm = normalize(promo.targetLabel);
        if (titleNorm && labelNorm === titleNorm) {
          log('� Match trouvé (label):', promo.title, 'pour', collectionTitle);
          return promo;
        }
      }
    }

    log('❌ Aucune promo trouvée pour:', collectionTitle);
    return null;
  }

  // Mettre à jour la bannière pour un panel
  async function updatePanelPromo(panel) {
    log('📍 updatePanelPromo appelé');
    log('   Panel:', panel.className);
    log('   Data-index:', panel.dataset.index);
    log('   Data-id:', panel.dataset.id);
    
    // Extraire le titre de la collection depuis le tab actif
    const tabsContainer = panel.closest('.section__content')?.previousElementSibling;
    log('   Tabs container:', !!tabsContainer);
    
    if (!tabsContainer) {
      // Essayer une autre méthode
      const allTabsContainers = document.querySelectorAll('.tabs-nav, [class*="tabs"]');
      log('   Containers tabs trouvés:', allTabsContainers.length);
      if (allTabsContainers.length > 0) {
        const activeTab = allTabsContainers[0].querySelector('.tabs__btn.active, button.active, [class*="tab"].active');
        if (activeTab) {
          const collectionTitle = activeTab.textContent?.trim() || '';
          log('   Collection (méthode alternative):', collectionTitle);
          await processPanelWithTitle(panel, collectionTitle);
          return;
        }
      }
    }
    
    const activeTab = tabsContainer?.querySelector('.tabs__btn.active, button.active');
    log('   Tab actif trouvé:', !!activeTab);
    
    const collectionTitle = activeTab?.textContent?.trim() || '';
    log('   Collection:', collectionTitle);

    if (!collectionTitle) {
      log('❌ Impossible de trouver le titre de collection');
      return;
    }

    await processPanelWithTitle(panel, collectionTitle);
  }
  
  async function processPanelWithTitle(panel, collectionTitle) {
    log('🔄 Traitement du panel pour:', collectionTitle);

    // Récupérer les données
    const [promos, customizations] = await Promise.all([
      fetchActivePromos(),
      fetchCustomizations()
    ]);
    
    log('   Promos récupérées:', promos.length);

    // Trouver la promo correspondante
    const promo = findPromoForCollection(promos, '', collectionTitle);
    log('   Promo trouvée:', promo ? promo.title : 'aucune');

    // Chercher les cartes promo existantes dans le panel
    const promoCards = panel.querySelectorAll('.promo-card, [class*="promo-card"]');
    log('   Cartes promo trouvées:', promoCards.length);

    if (promoCards.length === 0) {
      log('   ⚠️  Aucune carte trouvée dans ce panel, skip');
      return;
    }

    // Travailler SEULEMENT avec la première carte
    const card = promoCards[0];
    
    // Si pas de promo trouvée, on laisse la carte par défaut (ne rien faire)
    if (!promo) {
      log('   ℹ️  Pas de promo pour cette collection, carte par défaut conservée');
      return;
    }
    
    const customization = customizations[promo.id] || {};
    
    log('   🎨 Personnalisation de la première carte (sur', promoCards.length, 'trouvées)...');
    log('      Dataset actuel:', card.dataset.promoCustomized);
    log('      Promo ID:', promo.id);
    
    // TOUJOURS mettre à jour (ne pas skip même si déjà personnalisée)
    // Car quand on change de tab, la carte peut afficher une autre promo
    
    // Marquer comme personnalisée
    card.dataset.promoCustomized = promo.id;
    
    // Appliquer l'image de fond
    if (customization.image) {
      card.style.backgroundImage = `url("${customization.image}")`;
      card.style.backgroundSize = 'cover';
      card.style.backgroundPosition = 'center';
      log('      � Image appliquée:', customization.image.substring(0, 50));
    }
    
    // Mettre à jour le titre
    const titleEl = card.querySelector('[data-promo-title], h3');
    if (titleEl) {
      if (customization.customTitle) {
        titleEl.textContent = customization.customTitle;
        log('      � Titre personnalisé:', customization.customTitle);
      } else {
        titleEl.textContent = promo.title;
        log('      � Titre par défaut:', promo.title);
      }
    } else {
      log('      ⚠️ Element titre non trouvé');
    }
    
    // Mettre à jour la valeur promo
    const valueEl = card.querySelector('[data-promo-value], .promo-value');
    if (valueEl) {
      if (customization.customText) {
        valueEl.textContent = customization.customText;
        log('      � Texte personnalisé');
      } else if (promo.value) {
        valueEl.textContent = '-' + promo.value;
        log('      � Valeur:', promo.value);
      }
    } else {
      log('      ⚠️ Element valeur non trouvé');
    }
    
    // Gérer le timer
    const timerEl = card.querySelector('[data-promo-timer]');
    if (timerEl && promo.endsAt) {
      timerEl.style.display = 'block';
      updatePromoTimer(timerEl, promo.endsAt);
    } else if (timerEl) {
      timerEl.style.display = 'none';
    }
    
    // Cacher le bouton magasins par défaut quand une promo est active
    const storesBtn = card.querySelector('[data-promo-stores]');
    if (storesBtn) {
      storesBtn.style.display = 'none';
    }
    
    // Gérer le code promo (le code est affiché dans le bouton)
    if (promo.code) {
      const copyBtn = card.querySelector('[data-promo-code]');
      
      if (copyBtn) {
        copyBtn.textContent = promo.code;
        copyBtn.style.display = 'inline-block';
        copyBtn.onclick = (e) => {
          e.preventDefault();
          navigator.clipboard.writeText(promo.code);
          const originalText = promo.code;
          copyBtn.textContent = '✓ Copié !';
          setTimeout(() => copyBtn.textContent = originalText, 2000);
        };
        log('      � Code promo:', promo.code);
      }
    }
    
    // Gérer le lien vers la collection
    const linkBtn = card.querySelector('[data-promo-link]');
    if (linkBtn && promo.targetUrl) {
      linkBtn.href = promo.targetUrl;
      linkBtn.style.display = 'inline-block';
      log('      � Lien:', promo.targetUrl);
    }
    
    log('� Carte personnalisée pour:', collectionTitle);
  }

  // Observer les changements de tabs
  function observeTabs() {
    log('👀 Mise en place de l\'observateur de tabs...');
    
    const tabsContainer = document.querySelector('.tabs-nav, [class*="tabs-nav"]');
    if (!tabsContainer) {
      log('❌ Tabs container non trouvé pour l\'observer');
      return;
    }

    const tabButtons = tabsContainer.querySelectorAll('.tabs__btn, button[role="tab"], [class*="tabs__btn"]');
    log('   Boutons de tabs trouvés:', tabButtons.length);
    
    tabButtons.forEach((btn, index) => {
      log('   - Ajout listener sur tab:', btn.textContent.trim());
      
      btn.addEventListener('click', () => {
        log('🖱️  Tab cliqué:', btn.textContent.trim(), '(index:', btn.dataset.index, ')');
        
        // Attendre que le panel soit visible
        setTimeout(() => {
          // Méthode 1: chercher par classe active
          let activePanel = document.querySelector('.tabs__panel.active, .tabs__panel[style*="block"]');
          
          // Méthode 2: par index
          if (!activePanel && btn.dataset.index) {
            activePanel = document.querySelector(`.tabs__panel[data-index="${btn.dataset.index}"]`);
            log('   Panel trouvé par index:', !!activePanel);
          }
          
          // Méthode 3: tous les panels, prendre celui qui correspond
          if (!activePanel) {
            const allPanels = document.querySelectorAll('.tabs__panel');
            log('   Panels totaux:', allPanels.length);
            if (allPanels[index]) {
              activePanel = allPanels[index];
              log('   Panel trouvé par position:', !!activePanel);
            }
          }
          
          if (activePanel) {
            log('   � Panel actif trouvé, mise à jour...');
            updatePanelPromo(activePanel);
          } else {
            log('   ❌ Aucun panel actif trouvé');
          }
        }, 200);
      });
    });
    
    log('� Observateur configuré sur', tabButtons.length, 'tabs');
  }

  // Initialisation
  async function init() {
    log('Initialisation...');
    log('URL:', window.location.href);

    // Chercher la section collections-display
    const sections = document.querySelectorAll('[data-section-type], section');
    log('Sections trouvées:', sections.length);
    
    sections.forEach(s => {
      log('- Section:', s.className, 'Type:', s.dataset.sectionType);
    });

    // Chercher les tabs
    const tabsContainer = document.querySelector('.tabs-nav');
    log('Tabs container trouvé:', !!tabsContainer);
    
    if (tabsContainer) {
      const tabs = tabsContainer.querySelectorAll('.tabs__btn');
      log('Nombre de tabs:', tabs.length);
      tabs.forEach(tab => {
        log('  - Tab:', tab.textContent.trim(), 'Index:', tab.dataset.index);
      });
    }

    // Chercher les panels
    const panels = document.querySelectorAll('.tabs__panel');
    log('Panels trouvés:', panels.length);
    panels.forEach((panel, i) => {
      log('  - Panel', i, ':', panel.dataset.index, 'ID:', panel.dataset.id);
    });

    // Mettre à jour le premier panel (visible par défaut)
    const firstPanel = document.querySelector('.tabs__panel[data-index="0"]');
    log('Premier panel trouvé:', !!firstPanel);
    
    if (firstPanel) {
      await updatePanelPromo(firstPanel);
    } else {
      log('❌ Premier panel introuvable !');
    }

    // Observer les changements de tabs
    observeTabs();
    
    log('� Prêt !');
  }

  // Lancer au chargement
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    setTimeout(init, 300);
  }

  // Exposer pour rechargements
  window.TrustPromoCollectionV3 = { init };

})();
